import type { CemeteryGraveStats } from "./cemeteryApi";
import { formatCount } from "./cemeteryFormat";

/**
 * Thứ tự đoạn bar là HỢP ĐỒNG hiển thị (spec §4.2): Known → Partial → Unknown, không sắp
 * theo số lượng. `identityCounts` là shape đóng của contract nên luôn đủ ba đoạn.
 */
import { identityStateLabel } from "../graves/graveVocabulary";

const IDENTITY_SEGMENTS: ReadonlyArray<
  Readonly<{ key: "KNOWN" | "PARTIAL" | "UNKNOWN"; className: string }>
> = [
  { key: "KNOWN", className: "s11-strip-seg-known" },
  { key: "PARTIAL", className: "s11-strip-seg-partial" },
  { key: "UNKNOWN", className: "s11-strip-seg-unknown" },
];

const IDENTITY_STATE_STRIP_LABELS: Readonly<Record<"KNOWN" | "PARTIAL" | "UNKNOWN", string>> = {
  KNOWN: "đã xác định danh tính",
  PARTIAL: "xác định một phần",
  UNKNOWN: "chưa xác định danh tính",
};

/**
 * Contract khai báo `identityCounts` đủ ba khoá, nhưng dữ liệu wire có thể thiếu/null khoá
 * (đã gặp trên fixture E2E của S11.6). Đọc phòng thủ để dải danh tính không bao giờ hiện
 * "NaN" — thiếu số thì nói 0, không bịa và không vỡ chữ.
 */
function readCount(
  counts: CemeteryGraveStats["identityCounts"],
  key: "KNOWN" | "PARTIAL" | "UNKNOWN",
): number {
  const value: unknown = counts[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/**
 * Dải danh tính — điểm nhấn duy nhất của màn hồ sơ. Với nghĩa trang liệt sĩ, câu hỏi nghiệp
 * vụ số một là "bao nhiêu mộ đã xác định được danh tính", nên nó đứng ngay dưới header thành
 * một bar tỷ lệ 100% kèm nhãn chữ đủ ba vế (kể cả vế 0): thông tin không bao giờ chỉ nằm ở màu.
 *
 * `graveStats` vắng mặt → KHÔNG dựng bar giả, chỉ thông điệp trung thực.
 */
export function IdentityStrip({ stats }: { readonly stats: CemeteryGraveStats | undefined }) {
  if (stats === undefined) {
    return (
      <section className="s11-identity-strip" aria-label="Danh tính hồ sơ mộ">
        <p className="s11-detail-hint">
          Chưa có số liệu thống kê theo tình trạng hồ sơ cho nghĩa trang này.
        </p>
      </section>
    );
  }

  const counts = stats.identityCounts;
  const segments = IDENTITY_SEGMENTS.map((segment) => ({
    ...segment,
    count: readCount(counts, segment.key),
  }));
  const summary = segments
    .map((segment) => `${formatCount(segment.count)} ${IDENTITY_STATE_STRIP_LABELS[segment.key] ?? identityStateLabel(segment.key).toLowerCase()}`)
    .join(" · ");

  return (
    <section className="s11-identity-strip" aria-label="Danh tính hồ sơ mộ">
      <p className="s11-strip-total">{`DANH TÍNH ${formatCount(stats.total)} MỘ`}</p>
      <div className="s11-strip-bar" role="img" aria-label={summary}>
        {segments.map((segment) => (
          <span
            key={segment.key}
            className={`s11-strip-seg ${segment.className}`}
            style={{ flexGrow: segment.count }}
          />
        ))}
      </div>
      <p className="s11-strip-legend">{summary}</p>
    </section>
  );
}
